import { gql } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { useState } from 'react';
import { toast } from 'sonner';

import { EditCheckboxField } from '../../../EditCheckboxField';
import { EditDialog } from '../../../EditDialog';
import { EditDynamicSelectField } from '../../../EditDynamicSelectField';
import { EditFormField } from '../../../EditFormField';
import type { ContactOtherFieldsData } from '../queries/contactOthersQuery';
import { GET_CONTACT_OTHERS } from '../queries/contactOthersQuery';

const UPDATE_CONTACT = gql`
  mutation UpdateContact($id: ID!, $values: ContactInput!) {
    updateContact(id: $id, values: $values) {
      ok
      contact {
        id
        gccEntertainment
        businessEducationAndNetworkingEventsList
        nycMonthlyDinnerList
        hickeyAnnualHolidayCard
        jacksonHickeyBirthdayInvite
        ipiMember
        ipiOptInDate
        mcgillAlumnus
        metcircleMember
      }
    }
  }
`;

const EVENTS_MEMBERSHIPS_FIELDS: Record<
  string,
  { apiField: string; isArray?: boolean; isBoolean?: boolean; isDate?: boolean }
> = {
  businessEducationAndNetworkingEventsList: {
    apiField: 'businessEducationAndNetworkingEventsList',
    isBoolean: true
  },
  gccEntertainment: { apiField: 'gccEntertainment' },
  hickeyAnnualHolidayCard: {
    apiField: 'hickeyAnnualHolidayCard',
    isBoolean: true
  },
  ipiMember: { apiField: 'ipiMember', isBoolean: true },
  ipiOptInDate: { apiField: 'ipiOptInDate', isDate: true },
  jacksonHickeyBirthdayInvite: {
    apiField: 'jacksonHickeyBirthdayInvite',
    isBoolean: true
  },
  mcgillAlumnus: { apiField: 'mcgillAlumnus', isBoolean: true },
  metCircleMember: { apiField: 'metcircleMember', isBoolean: true },
  nycMonthlyDinnerList: { apiField: 'nycMonthlyDinnerList', isBoolean: true }
};

export interface EditEventsMembershipsDialogProps {
  contactId: string;
  initialValue: ContactOtherFieldsData;
  isOpen: boolean;
  onChange: (data: ContactOtherFieldsData) => void;
  onOpenChange: (open: boolean) => void;
  onSave: () => void;
  value: ContactOtherFieldsData;
}

export function EditEventsMembershipsDialog({
  contactId,
  initialValue,
  isOpen,
  onChange,
  onOpenChange,
  onSave,
  value
}: EditEventsMembershipsDialogProps) {
  const [isSaving, setIsSaving] = useState(false);

  const [updateContact] = useMutation(UPDATE_CONTACT, {
    refetchQueries: [{ query: GET_CONTACT_OTHERS, variables: { contactId } }]
  });

  const changes = getChangedValues(value, initialValue);
  const isDirty = Object.keys(changes).length > 0;

  const updateField = <K extends keyof ContactOtherFieldsData>(
    field: K,
    fieldValue: ContactOtherFieldsData[K]
  ) => {
    onChange({ ...value, [field]: fieldValue });
  };

  const handleSave = async () => {
    if (!isDirty) {
      onSave();
      return;
    }

    setIsSaving(true);

    try {
      const response = await updateContact({
        variables: { id: contactId, values: changes }
      });

      const data = response.data as {
        updateContact?: { ok?: boolean };
      };

      if (data.updateContact?.ok === false) {
        toast.error('Failed to save events & memberships');
      } else {
        toast.success('Events & memberships saved successfully');
        onSave();
      }
    } catch (error) {
      console.error('Error saving events & memberships:', error);
      toast.error('Failed to save events & memberships');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <EditDialog
      isDirty={isDirty}
      isOpen={isOpen}
      isSaving={isSaving}
      onOpenChange={onOpenChange}
      onSave={handleSave}
      title="Edit Events & Memberships"
    >
      <div className="grid grid-cols-2 gap-6">
        <EditDynamicSelectField
          fieldName="gcc_entertainment"
          label="GCC Entertainment"
          model="res.partner"
          onChange={v => {
            updateField('gccEntertainment', v);
          }}
          value={value.gccEntertainment}
        />
        <EditCheckboxField
          checked={value.businessEducationAndNetworkingEventsList}
          label="Business Education and Networking Events List"
          onCheckedChange={v => {
            updateField('businessEducationAndNetworkingEventsList', v);
          }}
        />
        <EditCheckboxField
          checked={value.nycMonthlyDinnerList}
          label="NYC Monthly Dinner List"
          onCheckedChange={v => {
            updateField('nycMonthlyDinnerList', v);
          }}
        />
        <EditCheckboxField
          checked={value.hickeyAnnualHolidayCard}
          label="Hickey Annual Holiday Card"
          onCheckedChange={v => {
            updateField('hickeyAnnualHolidayCard', v);
          }}
        />
        <EditCheckboxField
          checked={value.jacksonHickeyBirthdayInvite}
          label="Jackson Hickey Birthday Invite"
          onCheckedChange={v => {
            updateField('jacksonHickeyBirthdayInvite', v);
          }}
        />
        <EditCheckboxField
          checked={value.ipiMember}
          label="IPI Member"
          onCheckedChange={v => {
            updateField('ipiMember', v);
          }}
        />
        <EditFormField
          label="IPI Opt-in Date"
          onChange={v => {
            updateField('ipiOptInDate', v);
          }}
          type="date"
          value={toDateInputValue(value.ipiOptInDate) ?? ''}
        />
        <EditCheckboxField
          checked={value.mcgillAlumnus}
          label="McGill Alumnus"
          onCheckedChange={v => {
            updateField('mcgillAlumnus', v);
          }}
        />
        <EditCheckboxField
          checked={value.metCircleMember}
          label="MetCircle Member"
          onCheckedChange={v => {
            updateField('metCircleMember', v);
          }}
        />
      </div>
    </EditDialog>
  );
}

function getChangedValues(
  current: ContactOtherFieldsData,
  initial: ContactOtherFieldsData
): Record<string, boolean | null | string> {
  const changes: Record<string, boolean | null | string> = {};

  for (const [uiField, config] of Object.entries(EVENTS_MEMBERSHIPS_FIELDS)) {
    const currentVal = current[uiField as keyof ContactOtherFieldsData];
    const initialVal = initial[uiField as keyof ContactOtherFieldsData];

    if (currentVal !== initialVal) {
      if (config.isBoolean) {
        changes[config.apiField] = currentVal as boolean;
      } else if (config.isDate) {
        changes[config.apiField] = toDateInputValue(currentVal as string);
      } else {
        const strVal = currentVal as null | string;
        changes[config.apiField] = strVal === '' ? null : (strVal ?? null);
      }
    }
  }

  return changes;
}

function toDateInputValue(dateStr: string): null | string {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return null;
  return date.toISOString().split('T')[0];
}
